import React from 'react';
import { StyleSheet, Text, View } from 'react-native';
import Icon from '@/components/Icon';

// Keep theme ink intact, including the softer foreground in true-black mode.
export function getGuideColors(theme, kind) {
  const dark = theme.status_bar_style !== 'dark';
  return {
    primary: theme.text_color_primary || (dark ? '#e8e8ed' : '#1d1d1f'),
    secondary: theme.text_color_secondary || (dark ? '#a1a1a6' : '#626269'),
    background: (dark ? theme.solid_color_secondary : theme.solid_color_primary) || (dark ? '#1c1c1e' : '#ffffff'),
    band: theme.solid_color_tertiary || (dark ? '#252527' : '#f5f5f7'),
    border: dark ? '#ffffff14' : '#00000014',
    accent: kind === 'recipe' ? (dark ? '#e1ad70' : '#935b24') : (dark ? '#9bbcec' : '#365f99'),
  };
}

export function GuideHeader({ kind, icon, title, description, metadata, colors }) {
  return (
    <View>
      <View style={styles.eyebrow}>
        <Icon name={icon} size={14} color={colors.accent} />
        <Text style={[styles.eyebrowText, { color: colors.accent }]}>{kind}</Text>
      </View>
      <Text accessibilityRole="header" style={[styles.title, { color: colors.primary }]}>{title}</Text>
      {!!description && <Text style={[styles.description, { color: colors.secondary }]}>{description}</Text>}
      {!!metadata && <Text style={[styles.metadata, { color: colors.secondary }]}>{metadata}</Text>}
    </View>
  );
}

export function GuideSummary({ items, details, colors }) {
  const visibleItems = items.filter(item => item.value);
  const visibleDetails = details.filter(item => item.value);
  if (!visibleItems.length && !visibleDetails.length) return null;
  return (
    <View style={[styles.summary, { borderColor: colors.border }]}>
      {visibleItems.length > 0 && (
        <View style={styles.stats}>
          {visibleItems.map((item, index) => (
            <View key={item.label} style={[
              styles.stat,
              index > 0 && [styles.statDivider, { borderColor: colors.border }],
            ]}>
              <Text style={[styles.statValue, { color: colors.primary }]}>{item.value}</Text>
              <Text style={[styles.statLabel, { color: colors.secondary }]}>{item.label}</Text>
            </View>
          ))}
        </View>
      )}
      {visibleDetails.length > 0 && (
        <View style={[styles.summaryDetails, visibleItems.length > 0 && styles.summaryDetailsBelow]}>
          {visibleDetails.map(item => (
            <Text key={item.label} style={[styles.summaryDetail, { color: colors.secondary }]}>
              {item.label} {item.value}
            </Text>
          ))}
        </View>
      )}
    </View>
  );
}

export function GuideSection({ title, detail, colors }) {
  return (
    <View style={styles.sectionHeader}>
      <Text accessibilityRole="header" style={[styles.sectionTitle, { color: colors.primary }]}>{title}</Text>
      {!!detail && <Text style={[styles.sectionDetail, { color: colors.secondary }]}>{detail}</Text>}
    </View>
  );
}

export function GuideTips({ title, tips, colors }) {
  if (!tips.length) return null;
  return (
    <View style={[styles.tipsBox, { backgroundColor: colors.band }]}>
      <View style={styles.tipsHeader}>
        <Icon name="lightbulb" size={14} color={colors.accent} />
        <Text style={[styles.tipsTitle, { color: colors.primary }]}>{title}</Text>
      </View>
      {tips.map((tip, index) => (
        <Text key={index} style={[styles.tipText, { color: colors.secondary }]}>{tip}</Text>
      ))}
    </View>
  );
}

export const guideStyles = StyleSheet.create({
  container: {
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    alignSelf: 'stretch',
    flexShrink: 1,
    borderWidth: 1,
    borderRadius: 24,
    marginVertical: 8,
  },
  captureTarget: { borderRadius: 24, padding: 22 },
  group: { marginBottom: 8 },
  groupTitle: { fontSize: 13, fontWeight: '600', lineHeight: 20, marginTop: 4, marginBottom: 6 },
  number: {
    width: 28,
    minHeight: 28,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
    paddingVertical: 3,
  },
  numberText: { fontSize: 12, fontWeight: '600', fontVariant: ['tabular-nums'] },
  bodyText: { flex: 1, minWidth: 0, fontSize: 15, lineHeight: 23 },
});

const styles = StyleSheet.create({
  eyebrow: { flexDirection: 'row', alignItems: 'center', marginBottom: 15 },
  eyebrowText: { fontSize: 11, fontWeight: '700', letterSpacing: 1.6, textTransform: 'uppercase', marginLeft: 8 },
  title: { fontSize: 26, fontWeight: '700', lineHeight: 32, letterSpacing: -0.7 },
  description: { fontSize: 15, lineHeight: 23, marginTop: 10 },
  metadata: { fontSize: 12, lineHeight: 20, marginTop: 12 },
  summary: { borderTopWidth: 1, borderBottomWidth: 1, paddingVertical: 18, marginTop: 22 },
  stats: { flexDirection: 'row' },
  stat: { flex: 1, minWidth: 0, paddingHorizontal: 6, alignItems: 'center' },
  statDivider: { borderLeftWidth: 1 },
  statValue: { fontSize: 18, fontWeight: '600', lineHeight: 25, letterSpacing: -0.3, textAlign: 'center', fontVariant: ['tabular-nums'] },
  statLabel: { fontSize: 12, lineHeight: 18, marginTop: 4, textAlign: 'center' },
  summaryDetails: { flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'center', columnGap: 18, rowGap: 6 },
  summaryDetailsBelow: { marginTop: 14 },
  summaryDetail: { fontSize: 12, lineHeight: 18, flexShrink: 1 },
  sectionHeader: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'baseline', justifyContent: 'space-between', gap: 8, marginTop: 24, marginBottom: 12 },
  sectionTitle: { fontSize: 18, fontWeight: '600', lineHeight: 24, letterSpacing: -0.3 },
  sectionDetail: { fontSize: 12, lineHeight: 18 },
  tipsBox: { borderRadius: 14, padding: 16, marginTop: 16 },
  tipsHeader: { flexDirection: 'row', alignItems: 'center', marginBottom: 4 },
  tipsTitle: { fontSize: 13, lineHeight: 20, fontWeight: '600', marginLeft: 8 },
  tipText: { fontSize: 13, lineHeight: 20, marginTop: 5 },
});
